import { useDeleteFacility } from '../hooks/useDeleteFacility'
import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import { Alert, Box, FacilityIcon, Flex, Modal, Text } from '@hivewatch/dark'

import { ListItemSkeleton } from '../../components/ListItemSkeleton'

interface DarkDeleteFacilityModalIProps {
  facilityId: string
  isOpen: boolean
  onClose: () => void
}

/**
 * Rebrand variant of {@link DeleteFacilityModal} built on the `@hivewatch/dark`
 * `Modal`. Rendered from the dark facilities table (`DarkFacilitiesTableModal`);
 * the light table keeps the legacy modal.
 */
export const DarkDeleteFacilityModal = ({
  facilityId,
  isOpen,
  onClose,
}: DarkDeleteFacilityModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { data, error, deleteFacility, hasUnresolvedIncidents, dataLoaded } =
    useDeleteFacility({ id: facilityId, onClose })

  // Facility could not be loaded.
  if (error) {
    return (
      <Modal
        colorScheme={colorScheme}
        isOpen={isOpen}
        onClose={onClose}
        onSave={onClose}
        saveButtonLabel={t('common:close')}
        size='compact'
        title={t('facilities:delete_title')}
      >
        {t('facilities:crud_facility_not_found')}
      </Modal>
    )
  }

  // Still loading the unresolved-incident check — no footer actions yet.
  if (!dataLoaded) {
    return (
      <Modal
        colorScheme={colorScheme}
        isOpen={isOpen}
        isSaveHidden
        onClose={onClose}
        size='compact'
        title={t('facilities:delete_modal_title')}
      >
        <ListItemSkeleton />
      </Modal>
    )
  }

  // Deletion blocked by active incidents — keep Delete visible but disabled.
  if (hasUnresolvedIncidents) {
    return (
      <Modal
        colorScheme={colorScheme}
        isOpen={isOpen}
        isSaveDestructive
        isSaveDisabled
        onClose={onClose}
        onSave={deleteFacility}
        saveButtonLabel={t('common:delete')}
        showCancelButton
        size='compact'
        title={t('facilities:delete_modal_title')}
      >
        {t('facilities:delete_active_incidents')}
      </Modal>
    )
  }

  // Confirm deletion.
  return (
    <Modal
      colorScheme={colorScheme}
      header={t('facilities:delete_confirm')}
      isOpen={isOpen}
      isSaveDestructive
      onClose={onClose}
      onSave={deleteFacility}
      saveButtonLabel={t('common:delete')}
      showCancelButton
      size='compact'
      title={t('facilities:delete_modal_title')}
    >
      <Flex align='center' gap='8px'>
        <Box
          css={{ '& svg': { width: '20px', height: '20px' } }}
          display='inline-flex'
        >
          <FacilityIcon />
        </Box>
        <Text fontWeight={500}>{data?.name}</Text>
      </Flex>
      <Box mt='16px'>
        <Alert
          colorScheme={colorScheme}
          description={t('common:all_deletions_permanent')}
          title={t('common:warning_title')}
          variant='warning'
        />
      </Box>
    </Modal>
  )
}
